import React, { useEffect, useState } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  MessageSquare,
  Tag,
  MapPin,
  ArrowRight,
  CalendarSearch,
  Zap,
  Star,
  Loader2,
  Bookmark,
  ShoppingCart
} from "lucide-react";
import PartChatDialog from "@/components/PartChatDialog";
import { useDispatch, useSelector } from "react-redux";
import {
  removeSavedPart,
  savePart,
  searchParts
} from "../store/parts/partsAction";
import {
  fetchMessages,
  fetchSellerById,
  updateIsRead
} from "../store/partChat/partChatActions";
import PartImageSlider from "../hooks/partImageSlider";
import PartPaymentModal from "../components/Modal/PartPaymentModal";
import { toast } from "../components/ui/use-toast";
import { openAuthDialog } from "../store/auth/authSlice";

const PartSearchResultsPage = ({ onOpenChange: controlledOnOpenChange }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { query, results, profile } = location.state || {};
  const {
    searchResults,
    searchLoadingMore,
    page,
    totalPages,
    saveLoading,
    savedParts,
    removeLoading
  } = useSelector((state) => state.parts);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [partDetail, setPartDetail] = useState();
  const { messages, isMessagesLoading, isSenderLoading } = useSelector(
    (state) => state.partChat
  );
  const setIsOpen = controlledOnOpenChange || setInternalIsOpen;

  const [selectedConversation, setSelectedConversation] = useState(null);

  const handleLoadMore = () => {
    if (page + 1 < totalPages) {
      dispatch(
        searchParts({
          searchTerm: query,
          page: page + 1
        })
      );
    }
  };

  const handleSaveClick = (part, isSaved) => {
    if (!isAuthenticated || !user) return;

    if (isSaved) {
      dispatch(removeSavedPart({ userId: user?.uid, partId: part?.id }));
    } else {
      dispatch(savePart({ userId: user?.uid, part: part }));
    }
  };

  const tierStyles = {
    featured: {
      icon: Tag,
      label: "Featured",
      bgColor: "bg-yellow-100 dark:bg-yellow-100",
      textColor: "text-yellow-700 dark:text-yellow-800",
      borderColor: "border-yellow-300 dark:border-yellow-800"
    }
  };

  const handleChatClick = async (part) => {
    if (!isAuthenticated || !user) {
      toast({
        title: "Login Required",
        description: "Please login to send a message to Seller.",
        variant: "destructive"
      });
      dispatch(
        openAuthDialog({
          showLoginFirstOrOnSuccess: true,
          redirectPage: window.location.pathname,
          onSuccessfulAuth: () => setIsOpen(true)
        })
      );
      return;
    }
    try {
      const sellerData = await dispatch(
        fetchSellerById({ userId: part?.sellerId })
      ).unwrap();

      const conversation = {
        partInfo: part,
        otherUserDetail: sellerData
      };

      dispatch(
        fetchMessages({
          buyerId: user?.uid,
          sellerId: part?.sellerId,
          partId: part?.id,
          currentUserId: user?.uid
        })
      );

      setTimeout(() => {
        dispatch(
          updateIsRead({
            currentUserId: user?.uid,
            receiverId: part?.sellerId,
            partId: part?.id
          })
        );
      }, 1000);

      setSelectedConversation(conversation);
    } catch (error) {
      console.error("Error fetching seller:", error);
    }
  };

  const handleBuyClick = (part) => {
    if (!isAuthenticated || !user) {
      if (!isAuthenticated) {
        toast({
          title: "Login Required",
          description: "Please login as buyer to buy Parts.",
          variant: "destructive"
        });
        dispatch(
          openAuthDialog({
            showLoginFirstOrOnSuccess: true,
            redirectPage: window.location.pathname,
            onSuccessfulAuth: () => setIsOpen(true)
          })
        );
      }
      return;
    }
    if (!part?.stripePaymentLink) {
      toast({
        title: "Payment Unavailable",
        description: "This seller has not added a Stripe payment link.",
        variant: "destructive"
      });
      return;
    }
    setPartDetail(part);
    setShowPaymentModal(true);
  };

  const getCurrentTier = (part) => {
    if (part?.isFeatured) return tierStyles.featured;
    return null;
  };

  const getPartImages = (part) => {
    if (part?.imageUrls?.length) return part.imageUrls;
    if (part?.imageReferences?.length) return part.imageReferences;
    if (part?.imageUrl) return [part.imageUrl];
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 }
  };

  return (
    <motion.div
      className="w-full"
      initial="hidden"
      animate="visible"
      variants={containerVariants}
    >
      <motion.div variants={itemVariants} className="mb-8">
        <div className="flex justify-between">
          <h1 className="text-3xl font-bold text-foreground">
            Search Results for: "{query}"
          </h1>
          <Button
            variant="outline"
            onClick={() => (profile ? navigate("/profile") : navigate("/"))}
            className="mb-6"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            {profile ? "Back to Profile" : " Back to Home"}
          </Button>
        </div>
        {searchResults.length === 0 && (
          <p className="text-muted-foreground mt-2">
            No parts found matching your criteria. Try refining your search or{" "}
            <Link
              to="/inquire-part"
              state={{ initialQuery: query }}
              className="text-primary hover:underline"
            >
              send an inquiry to sellers
            </Link>
            .
          </p>
        )}
      </motion.div>

      {searchResults?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {searchResults?.map((part, index) => {
            const tier = getCurrentTier(part);
            const isSaved = savedParts?.some(
              (saved) => saved.partId === part?.id && saved.userId === user?.uid
            );

            return (
              <motion.div key={part.id || index} variants={itemVariants}>
                <Card
                  onClick={() => navigate(`/partDetail/${part?.id}`)}
                  className="h-full group hover:border-primary flex flex-col cursor-pointer overflow-hidden shadow-lg hover:shadow-primary/20 transition-shadow duration-300"
                >
                  <CardHeader>
                    <div className="aspect-video bg-muted rounded-md mb-3 flex items-center justify-center relative">
                      <PartImageSlider
                        images={getPartImages(part)}
                        alt={`Image of ${part.name}`}
                      />
                      {tier && (
                        <span
                          className={`absolute top-1.5 right-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${tier.bgColor} ${tier.textColor} ${tier.borderColor}`}
                        >
                          <tier.icon className="mr-1 h-2.5 w-2.5" />
                          {tier.label}
                        </span>
                      )}
                      {partData?.isFounderMember && (
                        <span
                          className={`absolute top-1.5 left-1.5 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] text-white font-semibold border border-yellow-100 bg-yellow-600`}
                        >
                          Founding Seller
                        </span>
                      )}
                    </div>
                    <div className="flex justify-between items-center">
                      <CardTitle className="text-lg group-hover:text-primary">
                        {part?.make == "other" ? part?.sub_make : part?.make}{" "}
                        {part?.model == "other" ? part?.sub_model : part?.model}{" "}
                        {part?.year}{" "}
                        {part?.name == "other" ? part?.sub_name : part?.name}
                      </CardTitle>
                      {user?.role === "buyer" && isAuthenticated && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSaveClick(part, isSaved);
                          }}
                          disabled={saveLoading || removeLoading}
                          className="transition-transform hover:scale-110"
                        >
                          {isSaved ? (
                            <Bookmark className="w-6 h-6 text-primary fill-primary drop-shadow-md" />
                          ) : (
                            <Bookmark className="w-5 h-5 text-muted-foreground hover:text-primary transition-colors duration-300" />
                          )}
                        </button>
                      )}
                    </div>
                    <CardDescription>
                      {part?.description ||
                        `High quality used ${part?.name_lowercase}.`}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="flex-grow space-y-2 text-sm">
                    <div className="flex items-center text-muted-foreground">
                      <Tag className="mr-2 h-4 w-4 text-primary" />
                      Price:{" "}
                      <span className="font-semibold text-foreground ml-1">
                        ${part?.price || (Math.random() * 200 + 50).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center text-muted-foreground">
                      <MapPin className="mr-2 h-4 w-4 text-primary" />
                      Seller:{" "}
                      <span className="font-semibold text-foreground ml-1">
                        {part.sellerName ||
                          `Seller #${Math.floor(Math.random() * 100 + 1)}`}
                      </span>
                    </div>
                    {/* <div className="flex items-center text-muted-foreground">
                      <CalendarSearch className="mr-2 h-4 w-4 text-primary" />
                      Year:{" "}
                      <span className="font-semibold text-foreground ml-1">
                        {part?.year || "N/A"}
                      </span>
                    </div> */}
                    {/* {(part?.make || part?.model) && (
                      <div className="text-xs text-muted-foreground pt-1">
                        {part?.make == "other" ? part?.sub_make : part?.make}{" "}
                        {part?.model == "other" ? part?.sub_model : part?.model}
                      </div>
                    )} */}
                    <div className="text-xs text-muted-foreground pt-1">
                      {part?.condition || "Condition: Used - Good"} |
                      Availability: {part?.availability || "In Stock"}
                    </div>
                  </CardContent>
                  {user?.role === "seller" &&
                  user?.uid === part?.sellerId ? null : (
                    <CardFooter className="gap-2">
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleChatClick();
                        }}
                        variant="outline"
                        size="sm"
                        className="w-full group"
                      >
                        <MessageSquare className="mr-2 h-4 w-4 group-hover:animate-pulse" />
                        Chat
                      </Button>
                      {part?.stripePaymentLink &&
                        part?.stripePaymentLink !== "" &&
                        user?.role !== "seller" && (
                          <Button
                            variant="default"
                            size="sm"
                            className="w-full group bg-primary text-white hover:bg-primary/70"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBuyClick();
                            }}
                          >
                            <ShoppingCart className="mr-2 h-4 w-4 group-hover:scale-110 transition-transform" />
                            Buy Now
                          </Button>
                        )}
                    </CardFooter>
                  )}
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}
      {page + 1 < totalPages && (
        <motion.div>
          <div className=" mt-12 flex justify-center">
            <Button
              variant="outline"
              onClick={handleLoadMore}
              disabled={searchLoadingMore}
              className="px-6 py-2"
            >
              {searchLoadingMore && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {searchLoadingMore ? "Loading..." : "Load More"}
            </Button>
          </div>
        </motion.div>
      )}
      {searchResults?.length == 0 && (
        <motion.div variants={itemVariants} className="mt-4 text-center">
          <p className="text-muted-foreground">
            Can't find what you're looking for?
          </p>
          {user?.role !== "seller" ? (
            <Link to="/inquire-part" state={{ initialQuery: query }}>
              <Button variant="outline" className="mt-2 group">
                Submit a Part Request{" "}
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          ) : (
            <Link to="/dashboard/add-part" state={{ initialQuery: query }}>
              <Button variant="outline" className="mt-2 group">
                Add this part to your inventory{" "}
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          )}
        </motion.div>
      )}
      {selectedConversation && (
        <PartChatDialog
          messages={messages}
          conversation={selectedConversation}
          isOpen={!!selectedConversation}
          isMessagesLoading={isMessagesLoading}
          isSenderLoading={isSenderLoading}
          onOpenChange={(open) => {
            if (!open) setSelectedConversation(null);
          }}
        >
          <></>
        </PartChatDialog>
      )}
      {showPaymentModal && (
        <PartPaymentModal
          open={showPaymentModal}
          part={partDetail}
          close={(isOpen) => {
            if (!isOpen) {
              setShowPaymentModal(false);
              setPartDetail(null);
            }
          }}
        />
      )}
    </motion.div>
  );
};

export default PartSearchResultsPage;
